import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FaCheck, FaTimes } from 'react-icons/fa';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ProviderCard from '../components/ProviderCard';
import { Helmet } from 'react-helmet-async';
import { toast } from "sonner";
import ImageSequenceScroller from "../components/ImageSequenceScroller";
import PlasmaBackground from "../components/PlasmaBackground";
import InfiniteScroll from '../components/InfiniteScroll';
import WhatsAppNotificationBanner from "../components/WhatsAppNotificationBanner";
import CountUp from '../components/CountUp';
import { Meteors } from '../components/ui/meteors';
import { Input } from "../components/ui/input"
import { Button } from "../components/ui/button"
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import SErraNDStepper, { Step } from '../components/Stepper';
import CustomStarRating from '../components/CustomStarRating';
import FuzzyText from '../components/FuzzyText';
import ChromaGrid from "../components/ChromaGrid";
import ShinyText from '../components/ShinyText';
import { captureInstallPrompt, triggerInstallPrompt, initializeFirebaseMessaging } from '../serviceWorkerRegistration';
import './Home.css';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { initializeFCM, setupForegroundNotifications } from '../utils/fcm';
import Navbar from '../components/Navbar';
import { Label } from "../components/ui/label";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLegend,
} from "../components/ui/field";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "../components/ui/item";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination"

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../components/ui/resizable"
import { Separator } from "../components/ui/separator";
import { Search, Star, HelpCircle, MessageSquare, AlertCircle, SearchX, MapPin, CheckCircle2, ShieldCheck, Check, X, ChevronDown, Clock, DollarSign, Briefcase, HeartHandshake, Users } from "lucide-react";


// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Component to invalidate map size
function MapInvalidate() {
  const map = useMap();
  useEffect(() => {
    if (map) {
      map.invalidateSize();
    }
  }, [map]);
  return null;
}

const API_URL = process.env.REACT_APP_API_URL;

// Fetch CSRF token from endpoint
let cachedCsrfToken = null;
let isFetchingCsrf = false;
const getCsrfToken = async () => {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }
  if (isFetchingCsrf) {
    await new Promise(resolve => setTimeout(resolve, 100));
    return cachedCsrfToken || '';
  }
  isFetchingCsrf = true;
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
    });
    cachedCsrfToken = response.data.csrfToken;
    return cachedCsrfToken;
  } catch (err) {
    console.error('[Home] CSRF Token fetch error:', err.response?.status, err.response?.data, err.message);
    return '';
  } finally {
    isFetchingCsrf = false;
  }
};

// Fetch providers
const fetchProviders = async ({ queryKey }) => {
  const [, page, searchQuery] = queryKey;
  const trimmedQuery = searchQuery.trim();
  const response = await axios.get(
    `${API_URL}/api/?page=${page}&per_page=8&ordering=-is_promoted,trust_score${trimmedQuery ? `&q=${encodeURIComponent(trimmedQuery)}` : ''}`,
    { withCredentials: true }
  );
  return response.data;
};

//Typography
const typography = {
  h1: "scroll-m-20 text-4xl font-extrabold tracking-tight text-balance text-center",
  h2: "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
  h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
  h4: "scroll-m-20 text-xl font-semibold tracking-tight",
  p: "leading-7 [&:not(:first-child)]:mt-6",
  lead: "text-xl text-muted-foreground",
  large: "text-lg font-semibold",
  small: "text-sm leading-none font-medium",
  muted: "text-sm text-muted-foreground",
};

// Fetch user profile
const fetchUser = async () => {
  try {
    const csrfToken = await getCsrfToken();
    if (!csrfToken) {
      console.error('[Home] No CSRF token available');
      return null;
    }
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/`, {
      withCredentials: true,
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
    });
    return response.data;
  } catch (err) {
    return null;
  }
};


// Fetch provider thumbnails with ETag support (for homepage cards)
const fetchProviderThumbnails = async ({ queryKey }, queryClient) => {
  const [, providerId] = queryKey;
  const cachedEtag = localStorage.getItem(`etag-thumbnails-${providerId}`) || '';
  try {
    const response = await axios.get(`${API_URL}/api/provider/${providerId}/thumbnails/`, {
      withCredentials: true,
      headers: { 'If-None-Match': cachedEtag },
    });

    // Store new ETag if provided
    if (response.headers['etag']) {
      localStorage.setItem(`etag-thumbnails-${providerId}`, response.headers['etag']);
    }

    return response.data.map(thumb => ({
      src: thumb.thumbnail_url,
      alt: `Thumbnail for provider ${providerId}`,
    }));
  } catch (error) {
    if (error.response?.status === 304) {
      // Use cached thumbnails
      const cachedData = queryClient.getQueryData(['providerThumbnails', providerId]);
      if (cachedData) return cachedData;
      // Fallback to empty array if no cache
      return [];
    }
    console.error('Thumbnail fetch error:', error);
    return [];  // Graceful fallback
  }
};

function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [userType, setUserType] = useState('');
  const [formData, setFormData] = useState({});
  const [showTutorial, setShowTutorial] = useState(() => !localStorage.getItem('tutorialCompleted'));
  const [errors, setErrors] = useState({});
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSwipeCue, setShowSwipeCue] = useState(() => {
    const shouldShow = !localStorage.getItem('hideSwipeCue') && !sessionStorage.getItem('hasSwiped');
    return shouldShow;
  });
  const [pulseComplete, setPulseComplete] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [showNotificationsPrompt, setShowNotificationsPrompt] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [hasPrompted, setHasPrompted] = useState(false);
  const [providerCount, setProviderCount] = useState(0);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(true);
  const navigate = useNavigate();
  const [showReferralOptions, setShowReferralOptions] = useState(false);
  const queryClient = useQueryClient();
  const loginFormRef = useRef(null);
  const registerFormRef = useRef(null);
  const [providers, setProviders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [openIndex, setOpenIndex] = useState(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    notNumeric: false,
    notSimilar: false,
    match: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I hire a service provider?",
      answer:
        "Search for the service you need, browse available gurus, and review their profiles, past work and ratings. Once you find the right fit, click 'Order' to send a request. ",
    },
    {
      question: "How do i become a service provider on SErraND?",
      answer:
        "Click the 'Register' button, choose to sign up as a Service Provider, and complete your profile with your services, location, availability, WhatsApp Notification number and your Business name. Once verified, you can start receiving job requests from real clients looking for your skills.",
    },
    {
      question: "How much commission does SErraND take?",
      answer:
        "SErraND takes a flat 10% commision from the provider after the job is completed and confirmed. Clients pay the agreed amount only. No hidden fees, no surprise charges. Our fee keeps the platform secure, trusted and running smoothly.",
    },
    {
      question: "Is SErraND available outside Nairobi?",
      answer:
        "Yes. SErraND is available in multiple towns across Kenya and continues to expand as more providers join. Availability may vary by location but we're actively growing coverage city by city based on demand. ",
    },
    {
      question: "How are providers vetted?",
      answer:
        "Every guru goes through identity verification and profile review before being listed. We also track performance using completed jobs, client ratings and Trust Scores. Providers who fail to meet our standards are removed from the platform. ",
    },
    {
      question: "Can I cancel an order?",
      answer:
        "Yes, before the provider accepts, it's free. After acceptance, cancellation fees may apply depending on how far the job has progressed. Check the order details.",
    },
    {
      question: "Why should i use SErraND instead of WhatsApp/Facebook or referrals?",
      answer:
        "SErraND removes uncertainty. You get verified providers, transparent pricing, documented requests and accountability through reviews and job history. No chasing replies, no disappearing fundis, no awkward disputes. Get a provider within 10 minutes!",
    },
  ];


  const getReferralIcon = (value) => {
    const map = {
      friend: 'fas fa-user-friends',
      admin_intro: 'fas fa-handshake',
      facebook: 'fab fa-facebook-f',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      tiktok: 'fab fa-tiktok',
      twitter: 'fab fa-x-twitter',
      google: 'fab fa-google',
      ai: 'fas fa-robot',
      poster: 'fas fa-bullhorn',
      radio_tv: 'fas fa-radio',
      other: 'fas fa-question-circle',
    };
    return map[value] || 'fas fa-question-circle';
  };

  const getReferralIconClass = (value) => {
    const brand = ['facebook', 'instagram', 'whatsapp', 'tiktok', 'twitter', 'google'];
    return brand.includes(value) ? 'text-blue-400' : 'text-green-400';
  };

  const getReferralLabel = (value) => {
    const map = {
      friend: 'Friend or Family',
      admin_intro: 'Admin Introduced Me',
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
      tiktok: 'TikTok',
      twitter: 'X / Twitter',
      google: 'Google Search',
      ai: 'AI (Grok, ChatGPT, etc.)',
      poster: 'Poster',
      radio_tv: 'Radio / TV',
      other: 'Other',
    };
    return map[value] || 'Tap to select';
  };

  const { data: providersData, isLoading: providersLoading, error: providersError, isFetching } = useQuery({
    queryKey: ['providers', page, searchQuery],
    queryFn: fetchProviders,
    keepPreviousData: true,
    cacheTime: 1000 * 60 * 30,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/categories/`, { withCredentials: true });
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { darkMode, toggleDarkMode } = useTheme();

  // Detect PWA status
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsPWA(isStandalone);
  }, []);

  // Restore pending booking after successful login
  useEffect(() => {
    if (!user) return;

    const pendingData = localStorage.getItem('pendingBooking');
    if (!pendingData) return;

    try {
      const booking = JSON.parse(pendingData);

      toast.success("Your booking details were saved! Opening the form...", {
        description: "Please review and confirm your order.",
        duration: 5000,
      });

      // Navigate back to provider page with restore data
      if (booking.providerId) {
        navigate(`/provider/${booking.providerId}?order=true`, {
          state: { restoreBooking: booking }
        });
      }

      localStorage.removeItem('pendingBooking');
    } catch (e) {
      console.error("Failed to restore pending booking:", e);
      localStorage.removeItem('pendingBooking');
    }
  }, [user, navigate]);

  // Fetch initial provider count
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/provider-count/`, { withCredentials: true })
      .then(response => response.json())
      .then(data => setProviderCount(data.total_count))
      .catch(error => console.error('Error fetching provider count:', error));
  }, []);

  // WebSocket for real-time provider count updates
  useEffect(() => {
    try {
      // Force 'ws:' for dev environment, use 'wss:' for production HTTPS
      const wsProtocol = process.env.NODE_ENV === 'development' ? 'ws:' : window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws/provider-count-updates/`;
      console.log('Attempting WebSocket connection to:', wsUrl); // Debug log
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => console.log('WebSocket connected for provider count');
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setProviderCount(data.total_count);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      ws.onerror = (error) => console.error('WebSocket error:', error);
      ws.onclose = () => console.log('WebSocket disconnected for provider count');
      return () => {
        ws.close();
      };
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
    }
  }, []);

  // Handle install prompt
  useEffect(() => {
    captureInstallPrompt((prompt) => {
      setCanInstall(true);
    });
  }, []);

  // Handle FCM token retrieval and foreground notifications
  useEffect(() => {
    if (
      user &&
      !hasPrompted &&
      'Notification' in window &&
      (isPWA || !/iPhone|iPad|iPod/.test(navigator.userAgent))
    ) {
      initializeFCM(user, setShowNotificationsPrompt, setHasPrompted);
    }

    const unsubscribe = setupForegroundNotifications(navigate);
    return () => unsubscribe();
  }, [user, hasPrompted, isPWA, navigate]);

  useEffect(() => {
    if (providersError) console.error('Providers fetch error:', providersError);
  }, [providersError]);

  useEffect(() => {
    if (providersData && (providersData.page === page || providersData.page === undefined)) {
      console.log('Updating providers:', { providersLength: providersData.providers?.length || 0, totalPages: providersData.pages || 1, page });
      setProviders(providersData.providers || []);
      setTotalPages(providersData.pages || 1);
    } else if (providersData) {
      console.log('Providers data mismatch:', { providersDataPage: providersData.page, currentPage: page });
    }
  }, [providersData, page]);

  useEffect(() => {
    const { username = '', password1 = '', password2 = '', location = '' } = formData;

    const newErrors = {};

    // Username validation
    if (username.length < 3 || !/^[a-zA-Z0-9._-]+$/.test(username)) {
      newErrors.username = 'At least 3 characters, letters/numbers/._- only';
    }

    // Location validation
    if (!location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));

    // Password requirements
    const pwReqs = {
      length: password1.length >= 8,
      notNumeric: password1.length > 0 && !/^\d+$/.test(password1),
      notSimilar:
        password1.length > 0 &&
        !password1.toLowerCase().includes(username.toLowerCase()) &&
        !password1.toLowerCase().includes((formData.email || '').split('@')[0].toLowerCase()),
      match: password1 === password2 && password2 !== '',
    };

    setPasswordRequirements(pwReqs);

    // Form is valid only if no errors in these fields + all password rules are met
    const allPwValid = Object.values(pwReqs).every(Boolean);
    const noRelevantErrors = !newErrors.username && !newErrors.location;
    setIsFormValid(noRelevantErrors && allPwValid);
  }, [formData]);

  const filteredProviders = providers;

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    navigate(`/?q=${searchQuery}`);
    handleScrollToTopGurus(e);
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem('tutorialCompleted', 'true');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: undefined });
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    setErrors({ ...errors, terms: undefined });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!userType) {
      setErrors({ all: ['Please select a user type (Regular User or Service Provider).'] });
      setShowUserTypeSelection(true);
      return;
    }
    if (userType === 'provider') {
      if (!formData.service_provided) {
        setErrors({ service_provided: ['Service offered is required for providers.'] });
        return;
      }
      if (!formData.business_name) {
        setErrors({ business_name: ['Business name is required for providers.'] });
        return;
      }
    }
    if (!termsAccepted) {
      setErrors({ terms: ['You must agree to the Terms of Service and Privacy Policy.'] });
      return;
    }
    setIsAuthLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });
    data.append('user_type', userType);
    try {
      await axios.post(`${API_URL}/api/register/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowRegister(false);
      setShowLogin(true);
      setTermsAccepted(false);
      setFormData({});
      setErrors({});
      setShowUserTypeSelection(true);
      setIsMenuOpen(false);
    } catch (error) {
      const errorData = error.response?.data?.errors || {
        all: ['Registration failed—please check your inputs or try again later.'],
      };
      setErrors(errorData);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/login/`, formData, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        }
      });

      queryClient.invalidateQueries(['user']);
      setShowLogin(false);
      setFormData({});
      setErrors({});
      setIsMenuOpen(false);
    } catch (error) {
      console.error('[Home] Login Error:', error.response?.status);
      setErrors(error.response?.data?.errors || { all: ['Login failed'] });
    } finally {
      setIsAuthLoading(false);
    }
  };


  const handleOrderClick = (providerId) => {
    navigate(`/provider/${providerId}?order=true`);
  };

  const handleScrollToTopGurus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const topGurusSection = document.getElementById('top-gurus');
    if (topGurusSection) {
      topGurusSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.error('Element #top-gurus not found in DOM');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const closeModal = () => {
    setShowLogin(false);
    setShowRegister(false);
    setFormData({});
    setErrors({});
    setTermsAccepted(false);
    setShowUserTypeSelection(true);
  };

  useEffect(() => {
    const hoverElements = document.querySelectorAll('.hover-text');
    hoverElements.forEach((el) => {
      const originalText = el.textContent;
      el.addEventListener('mouseover', () => (el.textContent = el.dataset.secret));
      el.addEventListener('mouseout', () => (el.textContent = originalText));
    });
    return () => hoverElements.forEach((el) => {
      el.removeEventListener('mouseover', () => {});
      el.removeEventListener('mouseout', () => {});
    });
  }, []);

  const pageTitle = "SErraND | Plug Wa Kazi - Connect with Trusted Service Providers in Kenya";
  const pageDescription = "Find reliable local service providers in Kenya with SErraND, your Plug Wa Kazi. From plumbing to deliveries, connect with vetted professionals for all your needs.";
  const pageKeywords = "SErraND, Plug Wa Kazi, service providers, Kenya, local services, hire professionals, trusted gurus, Nairobi, errands, hustlers, mtu wa kazi, kazi, mboka, work, service, services near me, workers";
  const canonicalUrl = "https://www.serrand.org";
  const ogImage = "https://www.serrand.org/static/images/og-image.jpg";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SErraND",
    "url": "https://www.serrand.org",
    "description": pageDescription,
    "publisher": {
      "@type": "Organization",
      "name": "SErraND",
      "url": "https://www.serrand.org",
      "logo": "https://www.serrand.org/images/logo512.png"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.serrand.org/?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };


  return (
    <div className="min-h-screen relative overflow-x-hidden bg-gray-50 dark:bg-neutral-950">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Marvin Useki" />
        <meta name="viewport" content="width=device-width, initial-scale=0.9, maximum-scale=1.0, user-scalable=yes" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="apple-touch-icon" href="/logo192.png" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Chef-in-Pink-Uniform.jpeg" />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Confident-Construction-Worker.jpeg" />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Focused-Worker-in-Blue.jpeg" />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Chef-in-Pink-Uniform.jpeg" />
        <meta name="twitter:image:alt" content="Chef in Pink Uniform" />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Confident-Construction-Worker.jpeg" />
        <meta name="twitter:image:alt" content="Confident Construction Worker" />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Focused-Worker-in-Blue.jpeg" />
        <meta name="twitter:image:alt" content="Focused Worker in Blue" />
        <meta name="twitter:site" content="@SErraND" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
      
     <Meteors count={60} angle={225} className="z-0" />

      {/* Login Modal */}
{showLogin && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-foreground">
            Welcome Back! 👋
          </h2>
          <button
            onClick={closeModal}
            className="text-muted-foreground hover:text-foreground text-2xl transition-colors"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <FieldGroup>
            <Field orientation="vertical">
              <FieldLabel htmlFor="username_or_email">Username or Email</FieldLabel>
              <FieldContent>
                <Input
                  id="username_or_email"
                  name="username_or_email"
                  onChange={handleInputChange}
                  placeholder="Enter username or email"
                  className="text-base"
                  required
                />
              </FieldContent>
            </Field>

            <Field orientation="vertical">
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <FieldContent>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="pr-12 text-base"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </FieldContent>
            </Field>
          </FieldGroup>

          {errors.all && Array.isArray(errors.all) && (
            <FieldError>{errors.all[0]}</FieldError>
          )}

          <div className="flex justify-between items-center text-sm">
            <Link
              to="/forgot-password"
              className="text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
            >
              Forgot Password?
            </Link>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isAuthLoading}
              className="flex-1"
              size="lg"
            >
              {isAuthLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Logging in...
                </div>
              ) : (
                'Login'
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
              size="lg"
            >
              Cancel
            </Button>
          </div>

          <div className="text-center pt-4 border-t">
            <p className="text-muted-foreground text-sm">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowLogin(false);
                  setShowRegister(true);
                }}
                className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
              >
                Register here
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {/* Register Modal */}
{showRegister && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-foreground">
            Join SErraND Squad! 🚀
          </h2>
          <button
            onClick={closeModal}
            className="text-muted-foreground hover:text-foreground text-2xl transition-colors"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {showUserTypeSelection ? (
  <div className="space-y-6">
    <p className="text-muted-foreground text-base text-center">
      Are you looking for services or offering them?
    </p>

    <div className="grid grid-cols-1 gap-4">
      <Button
        variant="default"
        className="h-auto py-4 px-5 text-left"
        onClick={() => {
          setUserType('user');
          setShowUserTypeSelection(false);
        }}
      >
        <div className="space-y-1">
          <div className="font-semibold text-white dark:text-white text-left">Join as Customer</div>
          <div className="text-xs text-white dark:text-white text-left text-muted-foreground leading-tight">
            Looking for help with errands or tasks
          </div>
        </div>
      </Button>

      <Button
        variant="outline"
        className="h-auto py-4 px-5 text-left"
        onClick={() => {
          setUserType('provider');
          setShowUserTypeSelection(false);
        }}
      >
        <div className="space-y-1">
          <div className="font-semibold text-base">Join as Service Provider</div>
          <div className="text-xs text-muted-foreground leading-tight">
            Offering services like plumbing, cleaning, deliveries...
          </div>
        </div>
      </Button>
    </div>

    <div className="text-center pt-4">
      <p className="text-muted-foreground text-sm">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
          className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
        >
          Login here
        </button>
      </p>
    </div>
  </div>
) : (
          <form onSubmit={handleRegister} className="space-y-6">
            <div className="text-sm text-muted-foreground mb-4">
              Registering as:{' '}
              <span className="font-semibold text-foreground">
                {userType === 'user' ? 'Regular User' : 'Service Provider'}
              </span>{' '}
              <button
                type="button"
                onClick={() => setShowUserTypeSelection(true)}
                className="text-primary hover:text-primary/80 underline text-sm ml-1"
              >
                Change
              </button>
            </div>

            {/* Core fields */}
            <FieldGroup>
              <Field orientation="vertical">
                <FieldLabel htmlFor="username">Username (min 3 chars)</FieldLabel>
                <Input
                  id="username"
                  name="username"
                  value={formData.username || ''}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  required
                />
                {errors.username && <FieldError>{errors.username}</FieldError>}
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                />
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="password1">Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="password1"
                    name="password1"
                    type={showPassword1 ? 'text' : 'password'}
                    value={formData.password1 || ''}
                    onChange={handleInputChange}
                    placeholder="Create a strong password"
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword1(!showPassword1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword1 ? 'Hide password' : 'Show password'}
                  >
                    {showPassword1 ? '🙈' : '👁️'}
                  </button>
                </div>
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="password2">Confirm Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="password2"
                    name="password2"
                    type={showPassword2 ? 'text' : 'password'}
                    value={formData.password2 || ''}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(!showPassword2)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword2 ? 'Hide password' : 'Show password'}
                  >
                    {showPassword2 ? '🙈' : '👁️'}
                  </button>
                </div>
              </Field>

              <FieldDescription>
                <ul className="text-sm space-y-1 mt-2">
                  <li className="flex items-center gap-2">
                    {passwordRequirements.length ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    At least 8 characters
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.notNumeric ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Not entirely numbers
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.notSimilar ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Not too similar to username or email
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.match ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Passwords match
                  </li>
                </ul>
              </FieldDescription>

              <Field orientation="vertical">
                <FieldLabel htmlFor="location">Location (e.g., Westlands)</FieldLabel>
                <Input
                  id="location"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleInputChange}
                  placeholder="Your location"
                  required
                />
                {errors.location && <FieldError>{errors.location}</FieldError>}
              </Field>
            </FieldGroup>

            {/* WhatsApp Number */}
            <Field orientation="vertical">
              <FieldLabel>WhatsApp Number (for order updates – recommended)</FieldLabel>
              <div className="flex border border-input rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                <div className="bg-muted px-4 py-3 text-muted-foreground font-medium border-r border-input flex items-center gap-2">
                  <i className="fab fa-whatsapp text-green-500" />
                  <span>+254</span>
                </div>
                <Input
                  name="whatsapp_notification_number"
                  type="tel"
                  placeholder="712345678"
                  value={(formData.whatsapp_notification_number || '').replace('+254', '')}
                  onChange={(e) => {
                    let num = e.target.value.replace(/\D/g, '').slice(0, 9);
                    handleInputChange({
                      target: {
                        name: 'whatsapp_notification_number',
                        value: num ? '+254' + num : ''
                      }
                    });
                  }}
                  className="flex-1 border-none focus:ring-0"
                  required={false}
                />
              </div>
            </Field>

            {/* Referral source */}
            <Field orientation="vertical">
              <FieldLabel>How did you hear about SErraND?</FieldLabel>
              <button
                type="button"
                onClick={() => setShowReferralOptions(!showReferralOptions)}
                className="w-full px-4 py-3 bg-muted border border-input rounded-lg text-left flex items-center justify-between hover:bg-accent transition-all text-sm"
              >
                <span className="flex items-center gap-3">
                  {formData.how_did_you_hear ? (
                    <>
                      <i
                        className={`fa-fw text-lg ${
                          ['facebook','instagram','whatsapp','tiktok','twitter','google'].includes(formData.how_did_you_hear)
                            ? 'fab'
                            : 'fas'
                        } fa-${getReferralIcon(formData.how_did_you_hear)} ${
                          formData.how_did_you_hear ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      />
                      <span>{getReferralLabel(formData.how_did_you_hear)}</span>
                      {formData.how_did_you_hear === 'other' && formData.how_did_you_hear_other && (
                        <span className="text-primary text-xs">({formData.how_did_you_hear_other})</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">Tap to select...</span>
                  )}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showReferralOptions ? 'rotate-180' : ''}`} />
              </button>

              {showReferralOptions && (
                <div className="mt-2 border border-input rounded-lg p-3 bg-muted/50 max-h-64 overflow-y-auto grid grid-cols-1 gap-2">
                  {[
                    { value: 'friend', icon: 'user-friends', label: 'Friend or Family', type: 'fas' },
                    { value: 'admin_intro', icon: 'handshake', label: 'Admin Introduced Me', type: 'fas' },
                    { value: 'facebook', icon: 'facebook-f', label: 'Facebook', type: 'fab' },
                    { value: 'instagram', icon: 'instagram', label: 'Instagram', type: 'fab' },
                    { value: 'whatsapp', icon: 'whatsapp', label: 'WhatsApp', type: 'fab' },
                    { value: 'tiktok', icon: 'tiktok', label: 'TikTok', type: 'fab' },
                    { value: 'twitter', icon: 'x-twitter', label: 'X / Twitter', type: 'fab' },
                    { value: 'google', icon: 'google', label: 'Google Search', type: 'fab' },
                    { value: 'ai', icon: 'robot', label: 'AI (Grok, ChatGPT, etc.)', type: 'fas' },
                    { value: 'poster', icon: 'bullhorn', label: 'Poster', type: 'fas' },
                    { value: 'radio_tv', icon: 'radio', label: 'Radio / TV', type: 'fas' },
                    { value: 'other', icon: 'circle-question', label: 'Other', type: 'fas' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          how_did_you_hear: opt.value,
                          how_did_you_hear_other: opt.value === 'other' ? prev.how_did_you_hear_other : ''
                        }));
                        setShowReferralOptions(false);
                      }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-all text-left w-full"
                    >
                      <i className={`fa-fw text-lg ${opt.type} fa-${opt.icon} text-muted-foreground`} />
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {formData.how_did_you_hear === 'other' && (
                <Input
                  name="how_did_you_hear_other"
                  placeholder="e.g. YouTube, Church, School..."
                  value={formData.how_did_you_hear_other || ''}
                  onChange={handleInputChange}
                  className="mt-3 text-sm"
                />
              )}
            </Field>

            {/* Provider-specific fields */}
            {userType === 'provider' && (
              <FieldGroup className="p-6 bg-muted/50 rounded-xl border border-border space-y-6">
                <FieldLegend variant="legend">Provider Details</FieldLegend>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="business_name">Brand Name</FieldLabel>
                  <Input
                    id="business_name"
                    name="business_name"
                    placeholder="e.g. Blessed Nail Art Service"
                    required
                  />
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="service_provided">Service Provided</FieldLabel>
                  <Input
                    id="service_provided"
                    name="service_provided"
                    placeholder="Be clear and precise (e.g., House hunting Service)"
                    required
                  />
                  {errors.service_provided && <FieldError>{errors.service_provided[0]}</FieldError>}
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="description">Description</FieldLabel>
                  <textarea
                    id="description"
                    name="description"
                    placeholder="Description - DO NOT input personal contact info"
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-ring min-h-[100px] resize-none"
                    onChange={handleInputChange}
                  />
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="business_hours">Work Hours</FieldLabel>
                  <Input
                    id="business_hours"
                    name="business_hours"
                    placeholder="e.g., 24/7, Mon-Sat 8am-6pm"
                  />
                </Field>
              </FieldGroup>
            )}

            {/* Terms */}
            <Field orientation="horizontal">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={handleTermsChange}
                  className="mt-1 h-4 w-4 text-primary border-input rounded focus:ring-primary"
                  required
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {errors.terms && <FieldError>{errors.terms}</FieldError>}
            </Field>

            {/* All other errors */}
            {errors.all && Array.isArray(errors.all) && (
              <FieldError>{errors.all[0]}</FieldError>
            )}
            {Object.entries(errors).map(([key, value]) => {
              if (key === 'all' || key === 'service_provided' || key === 'terms' || !Array.isArray(value)) return null;
              return <FieldError key={key}>{value[0]}</FieldError>;
            })}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isAuthLoading} className="flex-1" size="lg">
                {isAuthLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Registering...
                  </div>
                ) : (
                  'Register'
                )}
              </Button>

              <Button type="button" variant="outline" onClick={closeModal} size="lg">
                Cancel
              </Button>
            </div>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowRegister(false);
                    setShowLogin(true);
                  }}
                  className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
                >
                  Login here
                </button>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  </div>
)}

      {/* Notifications Prompt - Enhanced */}
      {user?.username && showNotificationsPrompt && 'Notification' in window && (
        <section className="py-6 px-4 max-w-4xl mx-auto relative z-10">
          <div className="relative bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl border border-gray-600 p-8 shadow-2xl backdrop-blur-lg">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl opacity-5"></div>
            <button
              onClick={() => {
                setShowNotificationsPrompt(false);
                setHasPrompted(true);
                localStorage.setItem('notificationPromptDismissed', 'true');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl transition-colors z-10"
              aria-label="Close notification prompt"
            >
              ×
            </button>

            <div className="relative z-10">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-4">Enable Notifications 📲 blown-up"></h2>
                <p className="text-lg text-gray-300 mb-4">
                  Stay updated with service requests, acceptances, and admin messages!
                </p>
                {/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA && (
                  <span className="block text-sm text-yellow-400 bg-yellow-400 bg-opacity-10 rounded-lg p-3 border border-yellow-400 border-opacity-30">
                    Add SErraND to your home screen to enable notifications on iOS.
                  </span>
                )}
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    Notification.requestPermission()
                      .then((permission) => {
                        setShowNotificationsPrompt(false);
                        setHasPrompted(true);
                        localStorage.setItem('notificationPromptDismissed', 'true');
                        if (permission === 'granted') {
                          initializeFCM(user, setShowNotificationsPrompt, setHasPrompted);
                        }
                      })
                      .catch((err) => {
                        console.error('Notification permission error:', err);
                        setShowNotificationsPrompt(false);
                        setHasPrompted(true);
                        localStorage.setItem('notificationPromptDismissed', 'true');
                      });
                  }}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA}
                >
                  Enable Notifications
                </button>
                <button
                  onClick={() => {
                    setShowNotificationsPrompt(false);
                    setHasPrompted(true);
                    localStorage.setItem('notificationPromptDismissed', 'true');
                  }}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Enhanced Navigation */}
      <Navbar 
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={() => setShowLogin(true)}
        openRegister={() => setShowRegister(true)}
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

{/* Hero Section */}
<section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-background">
  {/* Plasma background */}
  <PlasmaBackground
    speed={0.3}
    lightColor="#3a425c"
    darkColor="#b0b8d0"
    lightOpacity={0.95}
    darkOpacity={0.65}
    direction="forward"
    scale={1.2}
    mouseInteractive={true}
  />

  {/* Overlay for extra readability */}
  <div className="absolute inset-0 bg-black/40 dark:bg-black/60 pointer-events-none" />

  {/* Foreground content */}
  <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight text-white mb-6">
      Hire Trusted Fundis & Local Service Providers Near You
    </h1>

    <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10">
      Find verified mama fuas, caregivers, electricians and more. Book in minutes, pay securely.
    </p>

    {/* Search form - Main CTA */}
    <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-3xl mx-auto mb-10">
      <div className="relative flex-1">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search errands, mechanics, makeup artist..."
          className="pl-11 py-6 text-base rounded-xl border-gray-300/50 bg-white/90 dark:bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 dark:text-white placeholder:text-gray-500"
        />
      </div>
      <Button
        type="submit"
        size="lg"
        className="px-10 py-6 text-white dark:text-white font-medium rounded-xl"
      >
        Search Services
      </Button>
    </form>

    {/* Subtle Provider CTA */}
    {!user && (
      <div className="text-center mb-8">
        <button
          onClick={() => {
            setUserType('provider');
            setShowRegister(true);
            setShowUserTypeSelection(false);
          }}
          className="text-sm text-white/70 hover:text-white transition-colors underline underline-offset-4"
        >
          Are you a Service Provider? Register here
        </button>
      </div>
    )}

    {/* Subtle Trust Signals - Very small and side-by-side */}
    <div className="mt-6 flex flex-wrap justify-center items-center gap-x-8 gap-y-2 text-sm text-white/70">
      <div className="flex items-center gap-1">
        <span className="font-semibold text-primary">
          <CountUp
            from={0}
            to={providerCount}
            separator=","
            duration={2}
          />+
        </span>
        <span>providers</span>
      </div>

      <div className="flex items-center gap-1">
        <span className="font-semibold text-primary">
          KSh <CountUp
            from={0}
            to={70000}
            separator=","
            duration={2}
          />+
        </span>
        <span>volume</span>
      </div>
    </div>
  </div>
</section>


      {/* Top Providers Section */}
<section id="top-gurus" className="py-12 px-4 max-w-7xl mx-auto relative z-10">
  {/* Heading block – same style as search and count */}
  <Item variant="outline" className="mb-10 p-6 md:p-8 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
    <ItemContent className="space-y-4 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
        Meet Our Top Gurus 🌟
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400">
        Trusted professionals ready to help you
      </p>
    </ItemContent>
  </Item>

  {/* Top Pagination (only show if multiple pages) */}
  {totalPages > 1 && (
    <div className="mb-8 flex justify-center">
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (page > 1 && !isFetching) setPage(page - 1);
              }}
              className={page === 1 || isFetching ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
            return (
              <PaginationItem key={pageNum}>
                <PaginationLink
                  href="#"
                  isActive={pageNum === page}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isFetching) setPage(pageNum);
                  }}
                >
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {totalPages > 5 && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          <PaginationItem>
            <PaginationNext 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (page < totalPages && !isFetching) setPage(page + 1);
              }}
              className={page === totalPages || isFetching ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )}

  {/* Content states */}
  {providersLoading && page === 1 ? (
    <div className="flex justify-center items-center py-16">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-lg text-gray-600 dark:text-gray-400">Loading amazing gurus...</p>
      </div>
    </div>
  ) : providersError ? (
    <Empty className="border border-dashed py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertCircle className="h-12 w-12 text-destructive" />
        </EmptyMedia>
        <EmptyTitle>Oops! Something went wrong</EmptyTitle>
        <EmptyDescription className="text-center">
          Failed to load providers. Please try again later.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </EmptyContent>
    </Empty>
  ) : filteredProviders.length === 0 ? (
    <Empty className="border border-dashed py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <SearchX className="h-12 w-12 text-muted-foreground" />
        </EmptyMedia>
        <EmptyTitle>No providers found</EmptyTitle>
        <EmptyDescription className="text-center">
          Try adjusting your search or check back later.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" onClick={() => setSearchQuery("")}>
          Clear Search
        </Button>
      </EmptyContent>
    </Empty>
  ) : (
    <>
      {/* Provider grid */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
  {filteredProviders.map((provider) => (
    <ProviderCard
      key={provider.id}
      provider={provider}
      handleOrderClick={handleOrderClick}
      categories={categories}  // ← pass the categories array here
    />
  ))}
</div>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="mt-12 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1 && !isFetching) setPage(page - 1);
                  }}
                  className={page === 1 || isFetching ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      isActive={pageNum === page}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isFetching) setPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {totalPages > 5 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationNext 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages && !isFetching) setPage(page + 1);
                  }}
                  className={page === totalPages || isFetching ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )}
</section>

{/* Why Choose SErraND Section */}
<section className="py-12 px-4 max-w-5xl mx-auto relative z-10">
  {/* Heading block */}
  <Item variant="outline" className="mb-10 p-6 md:p-8 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
    <ItemContent className="space-y-4 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
        Why Choose SErraND?
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400">
        We're not just another app – we're your digital mtaani!
      </p>
    </ItemContent>
  </Item>

  {/* Resizable Comparison */}
  <ResizablePanelGroup
    direction="horizontal"
    className="min-h-[420px] md:min-h-[500px] rounded-xl border bg-card shadow-sm overflow-hidden"
  >
    {/* Left Panel - For Customers */}
    <ResizablePanel defaultSize={50} minSize={30}>
      <div className="h-full p-6 md:p-8 flex flex-col">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">For Customers</h3>
            <p className="text-muted-foreground">Find reliable help fast</p>
          </div>
        </div>

        <div className="space-y-6 flex-1">
          {[
            { icon: Clock, title: "Fast Matching", desc: "Get connected to nearby gurus in minutes" },
            { icon: ShieldCheck, title: "Vetted Professionals", desc: "All providers are background-checked" },
            { icon: DollarSign, title: "Fair Pricing", desc: "Only 10% commission – you keep most" },
            { icon: HeartHandshake, title: "Secure & Reliable", desc: "Protected payments & ratings system" },
          ].map((item, idx) => (
            <div key={idx} className="flex gap-4">
              <item.icon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ResizablePanel>

    <ResizableHandle withHandle className="bg-gray-200 dark:bg-gray-800" />

    {/* Right Panel - For Providers */}
    <ResizablePanel defaultSize={50} minSize={30}>
      <div className="h-full p-6 md:p-8 flex flex-col">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
            <Briefcase className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">For Service Providers</h3>
            <p className="text-muted-foreground">Earn more with less hassle</p>
          </div>
        </div>

        <div className="space-y-6 flex-1">
          {[
            { icon: DollarSign, title: "Low 10% Commission", desc: "Keep 90% of every job – fair & transparent" },
            { icon: Clock, title: "Flexible Schedule", desc: "Work when you want, accept jobs you like" },
            { icon: Users, title: "Steady Work Flow", desc: "Get matched with clients nearby daily" },
            { icon: CheckCircle2, title: "Build Reputation", desc: "Ratings & reviews help you stand out" },
          ].map((item, idx) => (
            <div key={idx} className="flex gap-4">
              <item.icon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ResizablePanel>
  </ResizablePanelGroup>
</section>

<section className="py-12 px-4 max-w-5xl mx-auto relative z-10">
  {/* Heading block – consistent with previous sections */}
  <Item variant="outline" className="mb-10 p-6 md:p-8 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
    <ItemContent className="space-y-4 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
        Frequently Asked Questions
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400">
        Quick answers to help you get started with SErraND | Plug Wa Kazi! 🇰🇪
      </p>
    </ItemContent>
  </Item>

  {/* FAQ Items – compact accordion */}
  <div className="max-w-4xl mx-auto space-y-4">
    {faqs.map((faq, index) => {
      const isOpen = openIndex === index;
      return (
        <div
          key={index}
          className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
        >
          <button
            type="button"
            onClick={() => toggleFAQ(index)}
            className="flex w-full items-center justify-between px-5 py-4 text-left focus:outline-none group"
          >
            <span className="text-base font-medium text-gray-900 dark:text-white group-hover:text-primary transition-colors">
              {faq.question}
            </span>
            <svg
              className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isOpen ? "max-h-96" : "max-h-0"
            }`}
          >
            <div className="px-5 pb-5 pt-1">
              <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {faq.answer}
              </p>
            </div>
          </div>
        </div>
      );
    })}
  </div>

  {/* Final paragraph – muted & compact */}
  <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8">
    If anything feels unclear, reach out. We build SErraND to be simple, transparent and reliable.{" "}
    <Link
      to="/contact"
      className="text-primary hover:text-primary/80 font-medium transition-colors underline"
    >
      Contact our support team
    </Link>
  </p>
</section>

      {/* Map Section */}
<section className="py-12 px-4 max-w-5xl mx-auto relative z-10">
  {/* Heading block – consistent style */}
  <Item variant="outline" className="mb-10 p-6 md:p-8 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
    <ItemContent className="space-y-4 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
        Find Us in Kenya 🗺️
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400">
        SErraND HQ – Where the magic happens!
      </p>
    </ItemContent>
  </Item>

  {/* Map Card */}
  <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow border-gray-200 dark:border-gray-800">
    <CardContent className="p-0">
      <div className="h-80 md:h-96 w-full">
        <MapContainer
          center={[-1.2921, 36.8219]}
          zoom={10}
          style={{ height: "100%", width: "100%" }}
          className="rounded-t-xl"  // only round top if needed
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker position={[-1.2921, 36.8219]}>
            <Popup className="custom-popup">
              <div className="text-center p-2 min-w-[180px]">
                <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-1">
                  SErraND HQ 🏢
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Nairobi, Kenya
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Connecting you with trusted service providers!
                </p>
              </div>
            </Popup>
          </Marker>
          <MapInvalidate />
        </MapContainer>
      </div>
    </CardContent>
  </Card>
</section>

      {/* Enhanced Footer */}
      <Footer 
        canInstall={canInstall}
        triggerInstallPrompt={triggerInstallPrompt}
      />
    </div>
  );
}

export default Home;
